import { Paper } from "@mui/material";
import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import AdView from "../../components/ui/AdView";
import { Ad_RefreshKey, BoardInfoState_CommentRegKey, CommunityBoardListState_BoardSeq, DialogMessageState, ImgViewer, IsBottomLoading, ToastMessageState } from '../../recoil/states';
import { asyncReqCommunityBoardGoodReg } from "../../reducers/CommunityBoardGoodRegReducer";
import { asyncReqCommunityBoardRemove, CommunityBoardRemoveActions } from "../../reducers/CommunityBoardRemoveReducer";
import { asyncReqCommunityBoardWarningReg } from "../../reducers/CommunityBoardWarningRegReducer";
import { AppDispatch, RootState } from "../../Store";
import { autoEmbedYouTubeHTML } from "../../utils/autoEmbedYouTube";
import CommonUtil from "../../utils/CommonUtil";
import ReplyList from "./ReplyList";
import ReplyReg from "./ReplyReg";

const BoardDetail = () => {
    const communityBoardDetailReducer_Result = useSelector((state: RootState) => state.CommunityBoardDetailReducer); // 게시물 상세 표시 처리를 위한 state
    const communityBoardWarningRegReducer_Result = useSelector((state: RootState) => state.CommunityBoardWarningRegReducer); // 게시물 신고 처리를 위한 state
    const communityBoardGoodRegReducer_Result = useSelector((state: RootState) => state.CommunityBoardGoodRegReducer); // 게시물 추천 처리를 위한 state
    const communityBoardRemoveReducer_Result = useSelector((state: RootState) => state.CommunityBoardRemoveReducer); // 게시물 삭제 처리를 위한 state
    const { MEM_UDID } = useSelector((state: RootState) => state.LoginBtnsReducer.asyncIsLogin); // 로그인 회원 고유번호를 갖는 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다이얼로그 메세지 요청 정보를 갖는 state
    const dispatch = useDispatch<AppDispatch>();
    const setToastMessageState = useSetRecoilState(ToastMessageState); // 토스트 메세지 요청 정보를 갖는 state
    const [warningCount, setWarningCount] = useState(0); // 신고 건 수
    const [goodCount, setGoodCount] = useState(0); // 추천 건 수

    const communityBoardListState_BoardSeq = useRecoilValue(CommunityBoardListState_BoardSeq); // 게시물 상세 UI 랜더링 처리를 위해 사용되는 state
    const ad_RefreshKey = useRecoilValue(Ad_RefreshKey); // 광고 UI 랜더링 처리를 위해 사용되는 state
    const boardInfoState_CommentRegKey = useRecoilValue(BoardInfoState_CommentRegKey); // 댓글등록 UI 랜더링 처리를 위해 사용되는 state
    const setImgViewer = useSetRecoilState(ImgViewer); // 이미지 뷰어 UI 랜더링 처리를 위해 사용되는 state
    const [isBottomLoading, setIsBottomLoading] = useRecoilState(IsBottomLoading); // 바닥 로딩바 표시 처리를 위해 사용되는 state

    const refBtnGoList = useRef<HTMLAnchorElement>(null); // 목록 버튼 참조 객체

    useEffect(() => {

        if (communityBoardDetailReducer_Result.status === "LOADING") {
            // 게시물 상세 > 통신 중 일 경우
        }
        else if (communityBoardDetailReducer_Result.status === "FAIL") {
            // 게시물 상세 > 통신 실패된 경우

            const message = "게시물 상세 가져오기 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (communityBoardDetailReducer_Result.status === "COMPLETE") {
            // 게시물 상세 > 통신 성공된 경우

            if (communityBoardDetailReducer_Result.n4ErrorCode !== 0) {
                // 에러가 발생한 경우

                const message = communityBoardDetailReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {
                // 정상인 경우

                setWarningCount(communityBoardDetailReducer_Result.INFO?.UCBM_WARNNING_COUNT || 0);
                setGoodCount(communityBoardDetailReducer_Result.INFO?.UCBM_GOOD_COUNT || 0);

                CommonUtil.scrollTo("#div_sub_title_ui", 0, 0); // 상단 메뉴 영역 안보이게 스크롤바 자동 이동
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityBoardDetailReducer_Result]);

    useEffect(() => {

        if (communityBoardWarningRegReducer_Result.status === "LOADING") {
            // 게시물 신고 > 통신 중 일 경우
        }
        else if (communityBoardWarningRegReducer_Result.status === "FAIL") {
            // 게시물 신고 > 통신 실패된 경우

            const message = "게시물 상세 가져오기 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (communityBoardWarningRegReducer_Result.status === "COMPLETE") {
            // 게시물 신고 > 통신 성공된 경우

            if (communityBoardWarningRegReducer_Result.n4ErrorCode !== 0) {
                // 에러가 발생한 경우

                const message = communityBoardWarningRegReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {
                // 정상인 경우
                const message = "신고 되었습니다.";
                setToastMessageState(rawState => ({ ...rawState, isShow: true, message }));
                setWarningCount((communityBoardDetailReducer_Result.INFO?.UCBM_WARNNING_COUNT || 0) + 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityBoardWarningRegReducer_Result]);

    useEffect(() => {

        if (communityBoardGoodRegReducer_Result.status === "LOADING") {
            // 게시물 추천 > 통신 중 일 경우
        }
        else if (communityBoardGoodRegReducer_Result.status === "FAIL") {
            // 게시물 추천 > 통신 실패된 경우

            const message = "게시물 상세 가져오기 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (communityBoardGoodRegReducer_Result.status === "COMPLETE") {
            // 게시물 추천 > 통신 성공된 경우

            if (communityBoardGoodRegReducer_Result.n4ErrorCode !== 0) {
                // 에러가 발생한 경우

                const message = communityBoardGoodRegReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {
                // 정상인 경우
                const message = "추천 되었습니다.";
                setToastMessageState(rawState => ({ ...rawState, isShow: true, message }));
                setGoodCount((communityBoardDetailReducer_Result.INFO?.UCBM_GOOD_COUNT || 0) + 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityBoardGoodRegReducer_Result]);

    useEffect(() => {

        if (communityBoardRemoveReducer_Result.status === "LOADING") {
            // 게시물 삭제 > 통신 중 일 경우
        }
        else if (communityBoardRemoveReducer_Result.status === "FAIL") {
            // 게시물 삭제 > 통신 실패된 경우

            const message = "게시물 삭제 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (communityBoardRemoveReducer_Result.status === "COMPLETE") {
            // 게시물 삭제 > 통신 성공된 경우

            dispatch(CommunityBoardRemoveActions.setInitStatus()); // 여기 영역이 중복 실행되지 않도록 초기화 실행

            if (communityBoardRemoveReducer_Result.n4ErrorCode !== 0) {

                const message = communityBoardRemoveReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else if (!communityBoardRemoveReducer_Result.isSuccess) {

                const message = "게시물 삭제 처리 중 알 수 없는 오류가 발생하였습니다.";
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {

                const message = "게시물이 삭제 되었습니다.";
                setToastMessageState(rawState => ({ ...rawState, isShow: true, message }));

                // 목록으로 이동
                (window as any).urlChange("/community_board_list?curr_page=" + CommonUtil.getQuerystring("curr_page", "1"));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityBoardRemoveReducer_Result]);

    /**
    * 목록 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns 
    */
    const handlerGoList = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        setIsBottomLoading(false); // 바닥 로딩바 숨김 처리

        const curr_page = CommonUtil.getQuerystring("curr_page", "");
        const search_type = CommonUtil.getQuerystring("search_type", "");
        const search_keyword = CommonUtil.getQuerystring("search_keyword", "");
        const sub_menu = CommonUtil.getQuerystring("sub_menu", "");

        let query_string = (curr_page === "" ? "" : "&curr_page=" + encodeURIComponent(curr_page))
            + (search_type === "" ? "" : "&search_type=" + encodeURIComponent(search_type))
            + (search_keyword === "" ? "" : "&search_keyword=" + encodeURIComponent(search_keyword))
            + (sub_menu === "" ? "" : "&sub_menu=" + encodeURIComponent(sub_menu))
            + (sub_menu === "" ? "" : "&sub_menu=" + encodeURIComponent(sub_menu))
            ;
        if (query_string !== "") query_string = "?" + query_string.substring(1);

        const url: string = "/community_board_list" + query_string;
        (window as any).urlChange(url);
    }

    /**
    * 신고 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns 
    */
    const handlerWarningReg = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            $(e.target).trigger("blur");
            return;
        }

        const message = "정말로 신고 하시겠습니까?";
        const callbackFuncOk = () => {
            dispatch(asyncReqCommunityBoardWarningReg({ board_seq: communityBoardListState_BoardSeq }));
        }
        setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: true, message, callbackFuncOk }));
    }

    /**
    * 추천 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns 
    */
    const handlerGoodReg = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            $(e.target).trigger("blur");
            return;
        }

        const message = "정말로 추천 하시겠습니까?";
        const callbackFuncOk = () => {
            dispatch(asyncReqCommunityBoardGoodReg({ board_seq: communityBoardListState_BoardSeq }));
        }
        setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: true, message, callbackFuncOk }));
    }

    /**
    * 삭제 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns 
    */
    const handlerBoardRemove = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            $(e.target).trigger("blur");
            return;
        }

        const message = "정말로 삭제 하시겠습니까?";
        const callbackFuncOk = () => {
            dispatch(asyncReqCommunityBoardRemove({ board_seq: communityBoardListState_BoardSeq }));
        }
        setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: true, message, callbackFuncOk }));
    }

    return (
        <div className="container community_ele_hide" id="div_info_ui" style={{ width: "100%" }}>

            <article className="bo_v" style={{ width: "100%", margin: "0px" }}>

                <header>
                    <h2 className="bo_v_title">
                        <span className="bo_v_tit" id="span_title" style={{ textAlign: "left" }}>
                            <div dangerouslySetInnerHTML={{ __html: communityBoardDetailReducer_Result.INFO?.UCBM_TITLE || '' }}></div>
                        </span>
                    </h2>
                </header>

                <section className="bo_v_info">
                    <div className="bo_v_info_l">
                        <strong><span className={communityBoardDetailReducer_Result.INFO?.UCBM_ANONYMOUS_YN == "Y" ? "sv_member anonymous" : "sv_member"} id="span_nickname">{communityBoardDetailReducer_Result.INFO?.UCBM_ANONYMOUS_YN == "Y" ? "익명" : communityBoardDetailReducer_Result.INFO?.UCBM_REG_NICKNAME}</span></strong>
                    </div>
                    <div className="bo_v_info_r">
                        <strong><i className="fa fa-users" aria-hidden="true"></i> <span id="span_hit_count">{communityBoardDetailReducer_Result.INFO?.UCBM_HIT_COUNT}</span></strong>
                        <strong><i className="fa fa-comments" aria-hidden="true"></i> <span id="span_comment_count">{communityBoardDetailReducer_Result.INFO?.UCBM_COMMENT_COUNT}</span></strong>
                        <strong className="if_date"><i className="fa fa-clock-o" aria-hidden="true"></i> <span id="span_board_reg_dt">{communityBoardDetailReducer_Result.INFO?.UCBM_REG_DT}</span></strong>
                    </div>
                </section>

                <section className="bo_v_atc">

                    <div style={{ width: "100%", height: "10px", float: "left" }}>&nbsp;</div>

                    {
                        (
                            communityBoardDetailReducer_Result.status === "COMPLETE" &&
                            communityBoardDetailReducer_Result.INFO?.UCBM_IMG_PATH !== ""
                        )
                        &&
                        <div className="bo_v_con" style={{ display: "none" }}>
                            <Paper sx={{ maxWidth: { md: "100%", xs: "100%" }, minWidth: { md: "500px", xs: "100%" }, border: "1px solid #000000" }}
                                elevation={0}
                                component="img"
                                className="img_add_file"
                                src={communityBoardDetailReducer_Result.INFO?.UCBM_IMG_PATH}
                                alt=""
                                onClick={(window as any).handleImageClick}
                                onLoad={(e) => $(e.currentTarget).parent().show()}
                            />
                        </div>
                    }

                    {(() => {
                        const raw = communityBoardDetailReducer_Result.INFO?.UCBM_CONTENT || "";
                        // 1) 자동 링크화(기존 로직 유지)
                        const linked = CommonUtil.autoLink(raw, { target: "_blank" });
                        // 2) 유튜브 링크 → iframe 임베드
                        const embedded = autoEmbedYouTubeHTML(linked);
                        // 3) XSS 방어: iframe 허용 속성 화이트리스트 추가
                        const safe = DOMPurify.sanitize(embedded, {
                            // iframe 태그 자체를 허용
                            ADD_TAGS: ["iframe"],
                            // iframe에서 쓸 속성들을 허용
                            ADD_ATTR: [
                                "src",
                                "title",
                                "sandbox",
                                "allow",
                                "allowfullscreen",
                                "referrerpolicy",
                                "loading",
                                "data-youtube-id"
                            ]
                        });
                        return (
                            <div
                                id="pre_content"
                                style={{ whiteSpace: "pre-wrap" }} // <pre> 대체: 줄바꿈/공백 유지
                                dangerouslySetInnerHTML={{ __html: safe }}
                            >
                                {/* 'DOMPurify.sanitize' 함수를 사용하면, <a>만 남고 <script>는 제거됨 - 터미널에서 'npm install dompurify' 입력해서 설치 필요 */}
                            </div>
                        );
                    })()}

                    <div style={{ width: "100%", height: "10px", float: "left" }}>&nbsp;</div>

                </section>

                <div style={{ width: "100%", height: "10px", float: "left" }}>&nbsp;</div>

                <div className="bo_v_top">

                    <ul className="bo_v_com">
                        <li id="li_remove_ui" style={communityBoardDetailReducer_Result.INFO?.UCBM_REG_MEM_UDID === MEM_UDID ? {} : { display: "none" }}><a href="#" onClick={handlerBoardRemove} className="btn_b03 btn"><div style={{ lineHeight: "20px", padding: "3px" }}><i className="fa fa-times" aria-hidden="true"></i><br />삭제</div></a></li>
                        <li><a href="#" ref={refBtnGoList} onClick={handlerGoList} className="btn_b01 btn"><div style={{ lineHeight: "20px", padding: "3px" }}><i className="fa fa-list-ul" aria-hidden="true"></i><br />목록</div></a></li>
                    </ul>

                    <ul className="bo_v_left">
                        <li><a href="#" onClick={handlerWarningReg} className="btn_b06 btn"><div style={{ lineHeight: "20px", padding: "3px" }}><i className="fa fa-exclamation-triangle" aria-hidden="true"></i><br />신고(<span id="span_warning_count" style={{ fontSize: "0.75em" }}>{warningCount}</span>)</div></a></li>
                        <li><a href="#" onClick={handlerGoodReg} className="btn_b06 btn"><div style={{ lineHeight: "20px", padding: "3px" }}><i className="fa fa-thumbs-o-up" aria-hidden="true"></i><br />추천(<span id="span_good_count" style={{ fontSize: "0.75em" }}>{goodCount}</span>)</div></a></li>
                    </ul>

                </div>

                <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_1", slot: "6469121842", height: CommonUtil.isMobileOs() ? 200 : 300 }}
                    kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_1", unit: "DAN-vbcea014av5c", width: 300, height: 250 }}
                    marginStyle="10px 0 0"
                    refreshKey={ad_RefreshKey}
                />

                <ReplyList />

                <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_2", slot: "9614159932", ...(CommonUtil.isMobileOs() ? { height: 200 } : { responsiveYn: "Y" }) }}
                    kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_2", unit: "DAN-qe1lvfmlg0u1", width: 300, height: 250 }}
                    marginStyle="10px 0 0"
                    refreshKey={ad_RefreshKey}
                />

                <nav className="bo_cate" id="navCommentListRoot" style={{ width: "100%" }}>
                    <ul className="bo_cate_ul">
                        <li>
                            <span className="cmt_btn">댓글등록</span>
                        </li>
                    </ul>
                </nav>
                <section className="bo_vc">
                    <ReplyReg key={boardInfoState_CommentRegKey} />
                </section>

            </article>

        </div>
    )
}

export default BoardDetail;
